import { useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'

export interface Vacancy {
  _id: string
  companyUserId: string
  companyName?: string
  title: string
  area?: string
  salaryMin?: number
  salaryMax?: number
  contractType?: string
  schedule?: string
  modality?: 'Presencial' | 'Remota' | 'Hibrida'
  benefits?: string
  requirements?: string
  activities?: string
  experience?: string
  education?: string
  skills?: string[]
  city?: string
  state?: string
  industry?: string
  status: 'publicada' | 'pausada' | 'cerrada'
  publishDate?: string
  closeDate?: string
  applicantsCount?: number
  createdAt: string
  updatedAt: string
}

export function useVacancies() {
  const [vacancies, setVacancies] = useState<Vacancy[]>([])
  const [loading, setLoading] = useState(false)

  const fetchByCompany = useCallback(async (companyUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.vacancies.list({
        filter: { companyUserId },
        sort: { createdAt: -1 },
      })
      setVacancies(list as Vacancy[])
    } catch (e) {
      console.error('Error al cargar vacantes de la empresa:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchPublic = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.vacancies.list({
        filter: { status: 'publicada' },
        sort: { createdAt: -1 },
      })
      setVacancies(list as Vacancy[])
    } catch (e) {
      console.error('Error al cargar vacantes publicas:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const createVacancy = useCallback(async (data: Partial<Vacancy>) => {
    const now = new Date().toISOString()
    const payload = {
      ...data,
      status: data.status || 'publicada',
      applicantsCount: 0,
      publishDate: data.publishDate || now,
      createdAt: now,
      updatedAt: now,
    }
    return await lumi.entities.vacancies.create(payload)
  }, [])

  const updateVacancy = useCallback(async (id: string, data: Partial<Vacancy>) => {
    return await lumi.entities.vacancies.update(id, {
      ...data,
      updatedAt: new Date().toISOString(),
    })
  }, [])

  const deleteVacancy = useCallback(async (id: string) => {
    return await lumi.entities.vacancies.delete(id)
  }, [])

  return {
    vacancies,
    loading,
    fetchByCompany,
    fetchPublic,
    createVacancy,
    updateVacancy,
    deleteVacancy,
  }
}
